import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api/client.js';
import { useDashboardData } from '../../state/DashboardDataContext.jsx';
import { platformLabel } from '../../lib/operations.js';
import {
  SETTINGS_ACCOUNTS_PATH,
  metricsConsentMissing,
  metricsConsentPlatformsFrom,
  startMetricsConsent,
} from '../../lib/metricsConsent.js';
import { BrandIcons } from '../pack-icons/brands';
import MetricsConsentDialog from './MetricsConsentDialog.jsx';
import '../../styles/social-metrics.css';

// GH-939: a TikTok account connected before the statistics scopes existed keeps
// publishing, but its views/likes/comments/shares stay empty until the owner
// grants the optional statistics permission. Settings already offers that
// consent per account; this banner surfaces the same account-pinned start on the
// screens a returning owner lands on and inside the post's performance block.
export const SNOOZE_KEY = 'dathent.tiktokStatisticsBanner.snoozed';
export const SNOOZE_MS = 24 * 60 * 60 * 1000;
const TIKTOK = new Set(['tiktok']);
const NAME_MAX = 32;
const NAMES_SHOWN = 3;

function readSnoozes() {
  try {
    const value = JSON.parse(window.localStorage.getItem(SNOOZE_KEY) || '{}');
    return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  } catch (_) { return {}; }
}

export function isSnoozed(snoozes, accountId, now = Date.now()) {
  const at = Number(snoozes?.[accountId]);
  return Number.isFinite(at) && at <= now && now - at < SNOOZE_MS;
}

function writeSnoozes(accountIds, now = Date.now()) {
  const next = {};
  for (const [id, at] of Object.entries(readSnoozes())) {
    if (isSnoozed({ [id]: at }, id, now)) next[id] = at;
  }
  for (const id of accountIds) next[id] = now;
  try { window.localStorage.setItem(SNOOZE_KEY, JSON.stringify(next)); } catch (_) { /* private mode */ }
  return next;
}

function accountName(account) {
  const name = String(account.displayName || account.externalAccountId || platformLabel(account.platform));
  return name.length > NAME_MAX ? `${name.slice(0, NAME_MAX - 1).trimEnd()}…` : name;
}

function accountNames(accounts, language) {
  const names = accounts.slice(0, NAMES_SHOWN).map(accountName);
  const rest = accounts.length - names.length;
  let list = names.join(', ');
  try { list = new Intl.ListFormat(language, { style: 'long', type: 'conjunction' }).format(names); } catch (_) { /* older engines */ }
  return rest > 0 ? `${list} +${rest}` : list;
}

// Last resolved app capability, kept for the current API read scope (a cache
// flush, sign-out or brand switch changes the scope and discards it), so a
// remount on the next screen renders the banner in its first frame instead of
// inserting it above the page after paint.
let capabilityCache = null;
function cachedConsentPlatforms() {
  return capabilityCache && capabilityCache.scope === api.getReadScope() ? capabilityCache.platforms : null;
}

// Connected TikTok accounts whose active token lacks the statistics scopes, on
// an app that can actually request them. Accounts come from the shared
// dashboard data (TTL-respecting, no new polling); the app capability rides the
// same cached GET /social/accounts and is only read when a candidate exists.
// Returns [] until both are known, so nothing flashes for anyone else.
export function useTikTokStatisticsAccounts(accountId = null) {
  const dash = useDashboardData() || {};
  const [consentPlatforms, setConsentPlatforms] = useState(cachedConsentPlatforms);

  useEffect(() => {
    Promise.resolve(dash.ensureDashboardData?.({ keys: ['socialAccounts'] })).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const candidates = (Array.isArray(dash.socialAccounts) ? dash.socialAccounts : [])
    .filter(account => (!accountId || account.id === accountId) && metricsConsentMissing(account, TIKTOK));
  const hasCandidates = candidates.length > 0;

  useEffect(() => {
    if (!hasCandidates || consentPlatforms) return undefined;
    let alive = true;
    const scope = api.getReadScope();
    Promise.resolve()
      .then(() => api.socialAccounts.list())
      .then((response) => {
        const platforms = metricsConsentPlatformsFrom(response);
        capabilityCache = { scope, platforms };
        if (alive) setConsentPlatforms(platforms);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [hasCandidates, consentPlatforms]);

  return consentPlatforms ? candidates.filter(account => metricsConsentMissing(account, consentPlatforms)) : [];
}

// placement="screen": top of Dashboard / Library / Calendar, snoozable for 24 h
// per account. placement="post": inside one post's performance block, pinned to
// that post's account and never snoozed; `fallback` (the block's own reconnect
// link) renders instead whenever the banner does not, so the remedy is offered
// exactly once.
export default function TikTokStatisticsBanner({ accountId = null, placement = 'screen', fallback = null }) {
  const { t, i18n } = useTranslation();
  const onPost = placement === 'post';
  const qualifying = useTikTokStatisticsAccounts(onPost ? accountId : null);
  const [snoozes, setSnoozes] = useState(readSnoozes);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [starting, setStarting] = useState(false);
  const [failed, setFailed] = useState(false);

  if (onPost && !accountId) return fallback;
  const visible = onPost ? qualifying : qualifying.filter(account => !isSnoozed(snoozes, account.id));
  if (!visible.length) return fallback;
  const first = visible[0];
  const settingsTo = onPost
    ? `/app/settings?${new URLSearchParams({ tab: 'accounts', metrics: 'permission', platform: 'tiktok', account: accountId })}`
    : SETTINGS_ACCOUNTS_PATH;

  const close = () => {
    setStarting(false);
    setConfirmOpen(false);
  };
  const start = async () => {
    setFailed(false);
    setStarting(true);
    let redirected = false;
    try {
      redirected = (await startMetricsConsent(first))?.redirected === true;
    } catch (_) {
      setFailed(true);
    }
    // The tab is already navigating to TikTok: keep "Opening TikTok…" on screen
    // until it leaves instead of flashing the idle banner back.
    if (!redirected) close();
  };

  return (
    <>
    <section className={`tsb${onPost ? ' tsb--post' : ''}`} role="status" data-tiktok-statistics-banner={placement}>
      <span className="tsb-mark" aria-hidden="true">{BrandIcons.tiktok({ size: onPost ? 16 : 20 })}</span>
      <div className="tsb-copy">
        <strong className="tsb-title">{t('socialMetrics.tiktokStatisticsTitle')}</strong>
        <p className="tsb-body">{t('socialMetrics.tiktokStatisticsBody', { account: accountNames(visible, i18n.language) })}</p>
        {failed && <p className="tsb-error" role="alert">{t('settings.connectionFailedToStart')}</p>}
      </div>
      <div className="tsb-actions">
        <button type="button" className="btn btn-primary btn-sm tsb-primary" disabled={starting} onClick={() => setConfirmOpen(true)}>
          {t('socialMetrics.enableTikTokStatistics')}
        </button>
        <Link className="tsb-link" to={settingsTo}>{t('settings.connectedSocialAccounts')}</Link>
        {!onPost && (
          <button type="button" className="btn btn-ghost btn-sm tsb-later" onClick={() => setSnoozes(writeSnoozes(visible.map(account => account.id)))}>
            {t('socialMetrics.tiktokStatisticsLater')}
          </button>
        )}
      </div>
    </section>
    <MetricsConsentDialog account={confirmOpen ? first : null} confirming={starting}
      onConfirm={start} onCancel={close} />
    </>
  );
}
